import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Wallet, Mail, Lock, User, ArrowRight, ShieldCheck, TrendingUp, PieChart } from "lucide-react";

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const { data } = await api.post(path, form);
      setUser(data.user);
      toast.success(mode === "login" ? "Welcome back!" : "Account created!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-indigo-600 p-12 text-white relative overflow-hidden">
        <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-indigo-500/40 blur-3xl" />
        <div className="absolute -left-10 bottom-10 w-72 h-72 rounded-full bg-violet-400/30 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-white/15 grid place-items-center backdrop-blur">
            <Wallet size={24} />
          </div>
          <span className="text-2xl font-heading font-extrabold">SyncPe</span>
        </div>
        <div className="relative space-y-6">
          <h1 className="text-4xl font-heading font-extrabold leading-tight">
            Your company finances,<br />beautifully in sync.
          </h1>
          <p className="text-indigo-100 text-lg max-w-md">
            Track money in and out, import bank statements, and get instant clarity on where your business stands.
          </p>
          <div className="space-y-3 pt-4">
            {[[TrendingUp, "Real-time cash flow & KPI dashboards"], [PieChart, "AI-powered transaction categorization"], [ShieldCheck, "Role-based access & isolated workspaces"]].map(([Icon, t], i) => (
              <div key={i} className="flex items-center gap-3 text-indigo-50">
                <div className="w-9 h-9 rounded-xl bg-white/10 grid place-items-center"><Icon size={18} /></div>
                <span>{t}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="relative text-indigo-200 text-sm">Premium fintech workspace for growing businesses.</p>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 grid place-items-center"><Wallet size={20} className="text-white" /></div>
            <span className="text-2xl font-heading font-extrabold">SyncPe</span>
          </div>
          <h2 className="text-2xl font-heading font-extrabold text-slate-900">
            {mode === "login" ? "Sign in to your account" : "Create your account"}
          </h2>
          <p className="text-slate-500 mt-1 mb-6 text-sm">
            {mode === "login" ? "Enter your credentials to continue" : "Get started with SyncPe in seconds"}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <Field icon={User} label="Full name" testid="name-input" value={form.name}
                onChange={(v) => setForm({ ...form, name: v })} placeholder="Jane Doe" />
            )}
            <Field icon={Mail} label="Email" testid="email-input" type="email" value={form.email}
              onChange={(v) => setForm({ ...form, email: v })} placeholder="you@company.com" />
            <Field icon={Lock} label="Password" testid="password-input" type="password" value={form.password}
              onChange={(v) => setForm({ ...form, password: v })} placeholder={"\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"} />

            <button type="submit" disabled={loading} data-testid="submit-btn"
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 transition-colors active:scale-[0.98] disabled:opacity-60">
              {loading ? "Please wait..." : (mode === "login" ? "Sign In" : "Create Account")}
              {!loading && <ArrowRight size={18} />}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400 font-medium">OR</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <button onClick={googleLogin} data-testid="google-login-btn"
            className="w-full border border-slate-200 bg-white hover:bg-slate-50 py-3 rounded-xl flex items-center justify-center gap-3 font-semibold text-slate-700 transition-colors">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
            Continue with Google
          </button>

          <p className="text-center text-sm text-slate-500 mt-6">
            {mode === "login" ? "Don't have an account? " : "Already have an account? "}
            <button data-testid="toggle-mode-btn" onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-indigo-600 font-semibold hover:underline">
              {mode === "login" ? "Sign up" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, testid, type = "text", value, onChange, placeholder }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</label>
      <div className="relative">
        <Icon size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          data-testid={testid}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required
          className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none transition-all text-slate-800"
        />
      </div>
    </div>
  );
}
